import { ENV } from './schema';

export * from './schema';

export const API_CONFIG = {
  baseUrl: ENV.VITE_API_URL.endsWith('/') ? ENV.VITE_API_URL.slice(0, -1) : ENV.VITE_API_URL,
  timeout: 30000,
  retryAttempts: 3,
  enableLogging: false,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
} as const;

export type ApiConfig = typeof API_CONFIG;

export const APP_CONFIG = {
  name: 'BST - The Soft Studio',
  shortName: 'BST',
  url: 'https://www.bstsoftstudio.in',
  logo: '/logo.png',
  description: 'Handcrafted soft toys made with love',
  version: '1.0.0',
  environment: ENV.VITE_MODE,
  isProduction: ENV.VITE_MODE === 'production',
  isDevelopment: ENV.VITE_MODE === 'development',
  logLevel: 'info',
} as const;

export type AppConfig = typeof APP_CONFIG;

export const AUTH_CONFIG = {
  tokenExpiry: 3600000,
  enablePersistentSession: true,
  maxLoginAttempts: 5,
  lockoutDuration: 900000,
  rateLimitLogin: 5,
  rateLimitWindow: 900000,
  avatarServiceUrl: 'https://api.dicebear.com/7.x/avataaars/svg',
  tokenKey: 'bst_token',
  refreshTokenKey: 'bst_refresh_token',
} as const;

export type AuthConfig = typeof AUTH_CONFIG;

export const SOCIAL_CONFIG = {
  instagram: 'https://www.instagram.com/bst_thesoftstudio',
  youtube: 'https://www.youtube.com/@bst-thesoftstudio',
  facebook: 'https://www.facebook.com/people/BST-The-Soft-Studio/61589529140209',
} as const;

export type SocialConfig = typeof SOCIAL_CONFIG;

export const CONTACT_CONFIG = {
  email: 'hello@bstsoftstudio.in',
  phone: '+91 98765 43210',
  address: 'Mumbai, Maharashtra, India',
} as const;

export type ContactConfig = typeof CONTACT_CONFIG;

export const ECOMMERCE_CONFIG = {
  shipping: {
    freeShippingThreshold: 2500,
    standardShippingCost: 299,
    expressShippingCost: 499,
    standardShippingDays: { min: 3, max: 5 },
    expressShippingDays: { min: 1, max: 2 },
  },
  tax: { enabled: false, rate: 0.08 },
  inventory: {
    lowStockThreshold: 5,
    enableBackorders: false,
  },
  giftWrapping: { enabled: true, cost: 3.99 },
  payment: {
    currency: 'INR',
    currencySymbol: '₹',
  },
  productFilters: { priceRange: { min: 0, max: 1000 }, defaultSort: 'featured' },
} as const;

export type EcommerceConfig = typeof ECOMMERCE_CONFIG;

export const UI_CONFIG = {
  animation: { defaultDuration: 0.3, staggerDelay: 0.05 },
  pagination: { defaultPageSize: 12, maxPageSize: 48 },
  images: {
    baseUrl: '',
    lazyLoadThreshold: '50px',
    quality: 85,
  },
  search: {
    debounceMs: 300,
    minQueryLength: 2,
    maxResults: 20,
  },
  toast: {
    duration: 5000,
    position: 'top-right' as 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left',
  },
} as const;

export type UiConfig = typeof UI_CONFIG;

export const ASSETS_CONFIG = {
  videoIntro: 'https://youtu.be/fo_62ph2zCo',
} as const;

export type AssetsConfig = typeof ASSETS_CONFIG;

export const API_ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',

  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
} as const;

export const parsePrice = (price: number | string): number => {
  if (typeof price === 'string') {
    if (price.includes('-')) {
      const parts = price.split('-').map(p => parseFloat(p.trim()));
      if (parts.length > 0 && !isNaN(parts[0])) return parts[0];
    }
    const val = parseFloat(price);
    return isNaN(val) ? 0 : val;
  }
  return price;
};

export const formatCurrency = (amount: number | string): string => {
  const locale = ECOMMERCE_CONFIG.payment.currency === 'INR' ? 'en-IN' : 'en-US';
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: ECOMMERCE_CONFIG.payment.currency,
    minimumFractionDigits: 2,
  });
  if (typeof amount === 'string') {
    if (amount.includes('-')) {
      const parts = amount.split('-').map(p => parseFloat(p.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return `${formatter.format(parts[0])} - ${formatter.format(parts[1])}`;
      }
    }
    const val = parseFloat(amount);
    if (!isNaN(val)) return formatter.format(val);
    return amount;
  }
  return formatter.format(amount);
};

export const calculateShipping = (subtotal: number, isExpress = false): number => {
  if (subtotal >= ECOMMERCE_CONFIG.shipping.freeShippingThreshold) return 0;
  return isExpress
    ? ECOMMERCE_CONFIG.shipping.expressShippingCost
    : ECOMMERCE_CONFIG.shipping.standardShippingCost;
};
